import styled from "@emotion/styled";
import { ImpactType } from "@ecologi/shared";
import { FilterButton } from "./ImpactFilterButton";
import { Typography } from "../Typography";
import UKFlag from "@ecologi/assets/icons/impact-shop/uk-flag.svg";
import ShadowSvg from "@ecologi/assets/icons/impact-shop/shadow.svg";

const FilterBar = styled.div`
  position: relative;
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
  gap: 8px;
  flex-wrap: wrap;
`;

const Icon = styled(
  ({
    icon,
    className,
  }: {
    icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    className?: string;
  }) => {
    const InnerIcon = icon;
    return <InnerIcon className={className} />;
  }
)`
  height: 52px;
  position: absolute;
  left: 8px;
  top: -20px;
`;

const Shadow = styled(ShadowSvg)<{ selected: boolean }>`
  position: absolute;
  top: 27px;
  left: 17px;
  opacity: ${({ selected }) => (selected ? 0.12 : 0.06)};
`;

const UkFilterButton = styled.button<{ selected?: boolean }>`
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors.forestLight : theme.colors.snowdrop};
  border-radius: 30px;
  border: none;
  padding: 14px 24px 14px 60px;
  margin: 8px 0;
  min-width: 96px;
  cursor: pointer;
  position: relative;

  :focus-visible {
    outline: ${(props) => `2px solid ${props.theme.colors.forest}`};
    outline-offset: 2px;
  }

  :hover {
    background-color: ${({ selected, theme }) =>
      selected ? theme.colors.forest : theme.colors.mistDark};
  }
`;

const UkFilterButtonText = styled(Typography)<{ selected: boolean }>`
  color: ${({ selected, theme }) =>
    selected ? theme.colors.snowdrop : theme.colors.charcoal};
`;

const TYPES = {
  [ImpactType.TREES]: {
    text: "Reforestation",
  },
  [ImpactType.CARBON_OFFSETS]: {
    text: "Carbon avoidance",
  },
  [ImpactType.CARBON_REMOVAL]: {
    text: "Carbon removal",
  },
  [ImpactType.HABITAT_RESTORATION]: {
    text: "Habitat restoration",
  },
};

export const ImpactShopFilterBar = ({
  impactTypes,
  toggleImpactType,
  clearImpactTypes,
  isUkOnly,
  setIsUkOnly,
  onFilterClick,
}: {
  impactTypes: ImpactType[];
  toggleImpactType: (impactType: ImpactType) => void;
  clearImpactTypes: () => void;
  isUkOnly: boolean;
  setIsUkOnly: (isUkOnly: boolean) => void;
  onFilterClick: (filterName: string) => void;
}) => {
  return (
    <FilterBar>
      <FilterButtons>
        <FilterButton
          impactType={null}
          selected={impactTypes.length === 0 && !isUkOnly}
          onClick={() => {
            clearImpactTypes();
            setIsUkOnly(false);
            onFilterClick("All");
          }}
        >
          All
        </FilterButton>
        <UkFilterButton
          selected={isUkOnly}
          onClick={() => {
            setIsUkOnly(!isUkOnly);
            onFilterClick("UK projects");
          }}
          data-testid="ukProjectsFilter"
        >
          <Icon icon={UKFlag} />
          <Shadow selected={isUkOnly} />
          <UkFilterButtonText use="body3" selected={isUkOnly}>
            UK projects
          </UkFilterButtonText>
        </UkFilterButton>
        {Object.entries(TYPES).map(([key, value]) => {
          return (
            <FilterButton
              key={key}
              selected={impactTypes.includes(key as ImpactType)}
              impactType={key as ImpactType}
              onClick={() => {
                toggleImpactType(key as ImpactType);
                onFilterClick(value.text);
              }}
            >
              {value.text}
            </FilterButton>
          );
        })}
      </FilterButtons>
    </FilterBar>
  );
};
